import React, { useState, useEffect } from "react";
import Sidebar from "../../components/commons/Sidebar";
import Swal from "sweetalert2";
import { getUsers, createUser, updateUser, deleteUser, toggleActive } from "../../api/usersAPI";

const EMPTY_FORM = { username: "", email: "", password: "", role: "admin" };

const UsersAdmin = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { mode: 'add'|'edit', data }
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch {
      Swal.fire({ icon: "error", text: "Failed to load users. (Superadmin only)" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setModal({ mode: "add" });
  };

  const openEdit = (u) => {
    setForm({ username: u.username, email: u.email, password: "", role: u.role });
    setModal({ mode: "edit", id: u.id });
  };

  const handleSave = async () => {
    if (!form.username.trim()) return Swal.fire({ icon: "warning", text: "Username is required." });
    if (modal.mode === "add" && !form.password.trim()) return Swal.fire({ icon: "warning", text: "Password is required for new users." });
    setSaving(true);
    try {
      const payload = { ...form };
      if (modal.mode === "edit" && !payload.password) delete payload.password;
      if (modal.mode === "add") {
        await createUser(payload);
      } else {
        await updateUser(modal.id, payload);
      }
      await load();
      setModal(null);
      Swal.fire({ icon: "success", title: "Saved!", timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: "error", text: err.response?.data?.message || "Failed to save." });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({ title: "Delete this user?", icon: "warning", showCancelButton: true, confirmButtonColor: "#ef4444", confirmButtonText: "Delete", cancelButtonText: "Cancel" })
      .then(async ({ isConfirmed }) => {
        if (!isConfirmed) return;
        try {
          await deleteUser(id);
          await load();
        } catch { Swal.fire({ icon: "error", text: "Failed to delete." }); }
      });
  };

  const handleToggle = async (id, current) => {
    try {
      await toggleActive(id, !current);
      await load();
    } catch { Swal.fire({ icon: "error", text: "Failed to update status." }); }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-900">
      <Sidebar />
      <div className="flex-1 p-6 lg:p-10 overflow-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-indigo-400">Superadmin</p>
            <h2 className="text-3xl font-black text-white">USER MANAGEMENT</h2>
          </div>
          <button onClick={openAdd} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl transition-colors">
            + New User
          </button>
        </div>

        {loading ? (
          <p className="text-zinc-500">Loading...</p>
        ) : (
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-800/50">
                <tr>
                  {["Username", "Email", "Role", "Status", "Aksi"].map((h) => (
                    <th key={h} className="text-left text-[10px] font-black uppercase tracking-widest text-zinc-500 px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-4 py-3 text-white font-semibold">{u.username}</td>
                    <td className="px-4 py-3 text-zinc-400">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${u.role === "superadmin" ? "bg-violet-500/20 text-violet-400" : "bg-blue-500/20 text-blue-400"}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggle(u.id, u.is_active)}
                        className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full transition-colors ${u.is_active ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30" : "bg-red-500/20 text-red-400 hover:bg-red-500/30"}`}
                      >
                        {u.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(u)} className="px-2 py-1 bg-zinc-800 hover:bg-indigo-600 text-white text-[10px] font-bold rounded-lg transition-colors">Edit</button>
                        <button onClick={() => handleDelete(u.id)} className="px-2 py-1 bg-zinc-800 hover:bg-red-600 text-white text-[10px] font-bold rounded-lg transition-colors">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal */}
        {modal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-md">
              <h3 className="text-lg font-black text-white mb-4">{modal.mode === "add" ? "Add User" : "Edit User"}</h3>
              <div className="space-y-4">
                {[["username","Username","text"],["email","Email","email"],["password",modal.mode === "add" ? "Password" : "New Password (leave blank to keep unchanged)","password"]].map(([key,lbl,type]) => (
                  <div key={key}>
                    <label className="text-[10px] font-bold text-zinc-400 uppercase block mb-1">{lbl}</label>
                    <input type={type} value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  </div>
                ))}
                <div>
                  <label className="text-[10px] font-bold text-zinc-400 uppercase block mb-1">Role</label>
                  <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="admin">Admin</option>
                    <option value="superadmin">Superadmin</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={handleSave} disabled={saving} className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-colors disabled:opacity-60">
                  {saving ? "Saving..." : "Save"}
                </button>
                <button onClick={() => setModal(null)} className="flex-1 py-2 bg-zinc-800 hover:bg-zinc-700 text-white font-bold rounded-xl transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersAdmin;
